import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  LevelUpSession,
  AdvancementChoice,
  createLevelUpSession,
  createTierAchievementExperience,
  saveLevelUpSession,
  loadLevelUpSession,
  clearLevelUpSession,
  getAdvancementSlotCost,
  DomainCardExchange,
  validateAdvancementChoice,
  markNextSlot,
  markNSlots,
  unmarkSlot
} from '../types/levelUp';
import { CharacterData } from '../types/character';
import {
  validateTierAchievement,
  validateLevelUpSessionCompletion,
  ValidationResult
} from '../utils/levelUpValidation';
import {
  getRemainingSlots,
  canAddAdvancement
} from '../utils/levelUpCalculations';
import { applyLevelUpToCharacter } from '../utils/levelUpCommit';
import {
  saveLevelUpSessionToFirebase,
  loadLevelUpSessionFromFirebase,
  deleteLevelUpSessionFromFirebase
} from '../utils/levelUpFirebase';

// ============================================================================
// WIZARD STEPS
// ============================================================================

export enum WizardStep {
  WELCOME = 0,
  TIER_ACHIEVEMENT = 1,
  ADVANCEMENTS = 2,
  DOMAIN_CARDS = 3,
  SUMMARY = 4,
  COMPLETION = 5
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface LevelUpStoreState {
  // Session state
  session: LevelUpSession | null;
  currentStep: WizardStep;
  character: CharacterData | null;

  // UI state
  isLoading: boolean;
  errors: string[];
  warnings: string[];

  // Actions - Session Management
  initializeSession: (character: CharacterData) => void;
  loadExistingSession: (characterId: string, character?: CharacterData) => Promise<boolean>;
  clearStore: () => void;

  // Firebase persistence
  saveToFirebase: () => Promise<void>;
  loadFromFirebase: (characterId: string) => Promise<boolean>;

  // Actions - Navigation
  setCurrentStep: (step: WizardStep) => void;
  goNext: () => void;
  goBack: () => void;
  canGoNext: () => boolean;
  canGoBack: () => boolean;

  // Actions - Step 1: Tier Achievement
  setTierAchievementExperience: (name: string) => void;

  // Actions - Step 2: Advancements
  addAdvancementChoice: (choice: AdvancementChoice) => void;
  removeAdvancementChoice: (index: number) => void;
  clearAdvancementChoices: () => void;

  // Actions - Step 2b: Advancements - Flag for additional domain card
  setAdditionalDomainCard: (hasAdditional: boolean) => void;

  // Actions - Step 3: Domain Cards (automatic damage thresholds)
  setNewDomainCards: (cardIds: string[]) => void;
  setCardExchange: (oldCardId: string, newCardId: string) => void;
  clearCardExchange: () => void;

  // Actions - Validation
  validateCurrentStep: () => ValidationResult;
  validateSession: () => ValidationResult;

  // Actions - Completion
  commitLevelUp: (onSuccess: (updatedCharacter: CharacterData) => void) => void;
  cancelWizard: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useLevelUpStore = create<LevelUpStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      session: null,
      currentStep: WizardStep.WELCOME,
      character: null,
      isLoading: false,
      errors: [],
      warnings: [],

      // ========================================================================
      // SESSION MANAGEMENT
      // ========================================================================

      initializeSession: (character: CharacterData) => {
        set({ isLoading: true, errors: [], warnings: [] });

        try {
          const session = createLevelUpSession(character.id || '', character);

          // Auto-apply damage threshold increase (Step 3 - automatic)
          session.damageThresholdsIncreased = true;

          // Always start at Welcome step (user navigates forward from there)
          set({
            session,
            character,
            currentStep: WizardStep.WELCOME,
            isLoading: false
          });

          // Save to localStorage
          saveLevelUpSession(session);

          // Also save to Firebase (async, don't wait)
          saveLevelUpSessionToFirebase(session).catch(err => {
            console.error('[LevelUp] Firebase save failed (non-blocking):', err);
          });
        } catch (error) {
          set({
            errors: [(error as Error).message],
            isLoading: false
          });
        }
      },

      loadExistingSession: async (characterId: string, character?: CharacterData) => {
        try {
          const { character: storedCharacter } = get();
          const charToUse = character || storedCharacter;

          // Try Firebase first, then localStorage
          let session = await loadLevelUpSessionFromFirebase(characterId);

          if (!session) {
            session = loadLevelUpSession(characterId);
          }

          if (!session) {
            return false;
          }

          // Validate that the session matches the current character level
          // If character is level 2, session should be for 2→3, not 1→2
          const currentLevel = parseInt(charToUse?.level || '1', 10);
          if (session.fromLevel !== currentLevel) {
            console.log(`[LevelUp] Discarding stale session: session is for ${session.fromLevel}→${session.toLevel}, but character is level ${currentLevel}`);

            // Delete the stale session
            clearLevelUpSession(characterId);
            deleteLevelUpSessionFromFirebase(characterId).catch(err => {
              console.error('[LevelUp] Failed to delete stale session:', err);
            });

            return false;
          }

          set({
            session,
            character: charToUse,
            currentStep: WizardStep.WELCOME,
            errors: [],
            warnings: []
          });

          return true;
        } catch (error) {
          console.error('[LevelUp] Failed to load session:', error);
          return false;
        }
      },

      clearStore: () => {
        const { session } = get();
        if (session) {
          clearLevelUpSession(session.characterId);

          // Also delete from Firebase (async, don't wait)
          deleteLevelUpSessionFromFirebase(session.characterId).catch(err => {
            console.error('[LevelUp] Firebase delete failed (non-blocking):', err);
          });
        }
        set({
          session: null,
          currentStep: WizardStep.WELCOME,
          character: null,
          errors: [],
          warnings: []
        });
      },

      // Firebase persistence actions
      saveToFirebase: async () => {
        const { session } = get();
        if (!session) return;

        try {
          await saveLevelUpSessionToFirebase(session);
        } catch (error) {
          console.error('[LevelUp] Failed to save to Firebase:', error);
          throw error;
        }
      },

      loadFromFirebase: async (characterId: string) => {
        try {
          const session = await loadLevelUpSessionFromFirebase(characterId);

          if (!session) {
            return false;
          }

          set({
            session,
            currentStep: WizardStep.WELCOME,
            errors: [],
            warnings: []
          });

          return true;
        } catch (error) {
          console.error('[LevelUp] Failed to load from Firebase:', error);
          return false;
        }
      },

      // ========================================================================
      // NAVIGATION
      // ========================================================================

      setCurrentStep: (step: WizardStep) => {
        set({ currentStep: step, errors: [], warnings: [] });
      },

      goNext: () => {
        const { currentStep, canGoNext, session } = get();

        if (!canGoNext()) {
          set({ errors: ['Cannot proceed to next step'] });
          return;
        }

        // Validate current step before proceeding
        const validation = get().validateCurrentStep();
        if (!validation.valid) {
          set({ errors: validation.errors });
          return;
        }

        // Special handling for Summary step: commit the level-up instead of just advancing
        if (currentStep === WizardStep.SUMMARY) {
          // This will be handled by the navigation component calling commitLevelUp
          // We shouldn't advance the step here - commitLevelUp will do it
          return;
        }

        let nextStep = currentStep + 1;

        set({ currentStep: nextStep, errors: [], warnings: [] });

        // Save session after each step
        if (session) {
          saveLevelUpSession(session);
        }
      },

      goBack: () => {
        const { currentStep, canGoBack, session } = get();

        if (!canGoBack()) {
          return;
        }

        let prevStep = currentStep - 1;

        set({ currentStep: prevStep, errors: [], warnings: [] });
      },

      canGoNext: () => {
        const { currentStep } = get();
        return currentStep < WizardStep.COMPLETION;
      },

      canGoBack: () => {
        const { currentStep } = get();
        return currentStep > WizardStep.WELCOME;
      },

      // ========================================================================
      // STEP 1: TIER ACHIEVEMENT
      // ========================================================================

      setTierAchievementExperience: (name: string) => {
        const { session } = get();

        if (!session?.tierAchievement) {
          return;
        }

        // ALWAYS update session regardless of validation (single source of truth)
        // Validation happens separately in validateCurrentStep for Next button control
        const newExperience = name.trim()
          ? createTierAchievementExperience(name.trim(), session.tierAchievement.level)
          : null;

        const updatedSession = {
          ...session,
          tierAchievement: {
            ...session.tierAchievement,
            newExperience,
            proficiencyIncreased: true
          }
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      // ========================================================================
      // STEP 2: ADVANCEMENTS
      // ========================================================================

      addAdvancementChoice: (choice: AdvancementChoice) => {
        const { session, character } = get();

        if (!session || !character) {
          set({ errors: ['No active session'] });
          return;
        }

        // Check if we can add this advancement
        if (!canAddAdvancement(session, choice)) {
          const cost = getAdvancementSlotCost(choice);
          const remaining = getRemainingSlots(session);
          set({
            errors: [`Not enough advancement slots. Need ${cost}, have ${remaining}.`]
          });
          return;
        }

        // Validate the choice (pass current choices to check for duplicate trait selections and tier achievement experience)
        const validation = validateAdvancementChoice(
          choice,
          character,
          session.tierAdvancementState,
          session.advancementChoices,
          session.tierAchievement?.newExperience,
          session.tierAchievement?.traitsCleared,
          session.toLevel // Pass target level for multiclass validation
        );

        if (!validation.valid) {
          set({ errors: validation.error ? [validation.error] : ['Invalid advancement choice'] });
          return;
        }

        // Add the choice
        const updatedSession = {
          ...session,
          advancementChoices: [...session.advancementChoices, choice]
        };

        // Mark the tier advancement slots as used
        const tierState = { ...updatedSession.tierAdvancementState };
        const slots = { ...tierState.slots };

        switch (choice.type) {
          case 'characterTraits': {
            const marked = markNextSlot(slots.characterTraits);
            if (marked) slots.characterTraits = marked;
            break;
          }
          case 'hitPoints': {
            const marked = markNextSlot(slots.hitPoints);
            if (marked) slots.hitPoints = marked;
            break;
          }
          case 'stress': {
            const marked = markNextSlot(slots.stress);
            if (marked) slots.stress = marked;
            break;
          }
          case 'experience': {
            const marked = markNextSlot(slots.experiences);
            if (marked) slots.experiences = marked;
            break;
          }
          case 'domainCard': {
            const marked = markNextSlot(slots.domainCard);
            if (marked) slots.domainCard = marked;
            break;
          }
          case 'evasion': {
            const marked = markNextSlot(slots.evasion);
            if (marked) slots.evasion = marked;
            break;
          }
          case 'subclassUpgrade': {
            const marked = markNextSlot(slots.subclassUpgrade);
            if (marked) slots.subclassUpgrade = marked;
            break;
          }
          case 'proficiency': {
            const marked = markNSlots(slots.proficiency, 2);
            if (marked) slots.proficiency = marked;
            break;
          }
          case 'multiclass': {
            const marked = markNSlots(slots.multiclass, 2);
            if (marked) slots.multiclass = marked;
            break;
          }
        }

        tierState.slots = slots;
        updatedSession.tierAdvancementState = tierState;

        // Apply mutual exclusion for subclass/multiclass
        if (choice.type === 'subclassUpgrade') {
          updatedSession.tierAdvancementState = {
            ...updatedSession.tierAdvancementState,
            crossedOut: {
              ...updatedSession.tierAdvancementState.crossedOut,
              multiclass: true
            }
          };
        } else if (choice.type === 'multiclass') {
          updatedSession.tierAdvancementState = {
            ...updatedSession.tierAdvancementState,
            crossedOut: {
              ...updatedSession.tierAdvancementState.crossedOut,
              subclassUpgrade: true
            }
          };
        }

        set({
          session: updatedSession,
          errors: [],
          warnings: []
        });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      removeAdvancementChoice: (index: number) => {
        const { session } = get();

        if (!session) return;

        const removedChoice = session.advancementChoices[index];
        const updatedChoices = session.advancementChoices.filter((_, i) => i !== index);

        let updatedTierState = { ...session.tierAdvancementState };
        const slots = { ...updatedTierState.slots };

        // Unmark the tier advancement slots
        switch (removedChoice.type) {
          case 'characterTraits': {
            const unmarked = unmarkSlot(slots.characterTraits);
            if (unmarked) slots.characterTraits = unmarked;
            break;
          }
          case 'hitPoints': {
            const unmarked = unmarkSlot(slots.hitPoints);
            if (unmarked) slots.hitPoints = unmarked;
            break;
          }
          case 'stress': {
            const unmarked = unmarkSlot(slots.stress);
            if (unmarked) slots.stress = unmarked;
            break;
          }
          case 'experience': {
            const unmarked = unmarkSlot(slots.experiences);
            if (unmarked) slots.experiences = unmarked;
            break;
          }
          case 'domainCard': {
            const unmarked = unmarkSlot(slots.domainCard);
            if (unmarked) slots.domainCard = unmarked;
            break;
          }
          case 'evasion': {
            const unmarked = unmarkSlot(slots.evasion);
            if (unmarked) slots.evasion = unmarked;
            break;
          }
          case 'subclassUpgrade': {
            const unmarked = unmarkSlot(slots.subclassUpgrade);
            if (unmarked) slots.subclassUpgrade = unmarked;
            break;
          }
          case 'proficiency': {
            // Unmark 2 slots for proficiency
            const unmarked1 = unmarkSlot(slots.proficiency);
            if (unmarked1) {
              const unmarked2 = unmarkSlot(unmarked1);
              if (unmarked2) slots.proficiency = unmarked2;
            }
            break;
          }
          case 'multiclass': {
            // Unmark 2 slots for multiclass
            const unmarked1 = unmarkSlot(slots.multiclass);
            if (unmarked1) {
              const unmarked2 = unmarkSlot(unmarked1);
              if (unmarked2) slots.multiclass = unmarked2;
            }
            break;
          }
        }

        updatedTierState.slots = slots;

        // Clear mutual exclusion if removing subclass/multiclass
        if (removedChoice.type === 'subclassUpgrade') {
          const hasOtherSubclass = updatedChoices.some(c => c.type === 'subclassUpgrade');
          if (!hasOtherSubclass) {
            updatedTierState.crossedOut.multiclass = false;
          }
        } else if (removedChoice.type === 'multiclass') {
          const hasOtherMulticlass = updatedChoices.some(c => c.type === 'multiclass');
          if (!hasOtherMulticlass) {
            updatedTierState.crossedOut.subclassUpgrade = false;
          }
        }

        const updatedSession = {
          ...session,
          advancementChoices: updatedChoices,
          tierAdvancementState: updatedTierState
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      clearAdvancementChoices: () => {
        const { session } = get();

        if (!session) return;

        const updatedSession = {
          ...session,
          advancementChoices: [],
          tierAdvancementState: {
            ...session.tierAdvancementState,
            crossedOut: {
              subclassUpgrade: false,
              multiclass: false
            }
          }
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      // ========================================================================
      // STEP 2B: ADDITIONAL DOMAIN CARD FLAG
      // ========================================================================

      setAdditionalDomainCard: (hasAdditional: boolean) => {
        const { session } = get();

        if (!session) return;

        const updatedSession = {
          ...session,
          hasAdditionalDomainCard: hasAdditional
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      // ========================================================================
      // STEP 3: DOMAIN CARDS
      // ========================================================================

      setNewDomainCards: (cardIds: string[]) => {
        const { session } = get();

        if (!session) return;

        const updatedSession = {
          ...session,
          newDomainCards: cardIds
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      setCardExchange: (oldCardId: string, newCardId: string) => {
        const { session } = get();

        if (!session) return;

        const exchange: DomainCardExchange = {
          oldCardId,
          newCardId
        };

        const updatedSession = {
          ...session,
          domainCardExchange: exchange
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      clearCardExchange: () => {
        const { session } = get();

        if (!session) return;

        const updatedSession = {
          ...session,
          domainCardExchange: null
        };

        set({ session: updatedSession });
        saveLevelUpSession(updatedSession);

        // Auto-save to Firebase (non-blocking)
        saveLevelUpSessionToFirebase(updatedSession).catch(err => {
          console.error('[LevelUp] Firebase auto-save failed:', err);
        });
      },

      // ========================================================================
      // VALIDATION
      // ========================================================================

      validateCurrentStep: () => {
        const { session, currentStep, character } = get();

        // Welcome step is always valid (session may not be initialized yet)
        if (currentStep === WizardStep.WELCOME) {
          return { valid: true, errors: [] };
        }

        if (!session || !character) {
          return { valid: false, errors: ['No active session'] };
        }

        switch (currentStep) {
          case WizardStep.TIER_ACHIEVEMENT:
            if (!session.tierAchievement) {
              return { valid: true, errors: [] }; // Skip if not applicable
            }
            return validateTierAchievement(
              session.tierAchievement.level,
              session.tierAchievement.newExperience?.name || '',
              character.experiences || []
            );

          case WizardStep.ADVANCEMENTS: {
            const slotsUsed = getRemainingSlots(session);
            if (slotsUsed !== 0) {
              return {
                valid: false,
                errors: [`You must use exactly 2 advancement slots. ${2 - slotsUsed} remaining.`]
              };
            }
            return { valid: true, errors: [] };
          }

          case WizardStep.DOMAIN_CARDS: {
            const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
            if (!session.newDomainCards || session.newDomainCards.length !== requiredCardCount) {
              return {
                valid: false,
                errors: [`You must select ${requiredCardCount} domain card${requiredCardCount > 1 ? 's' : ''}`]
              };
            }
            return { valid: true, errors: [] };
          }

          case WizardStep.SUMMARY:
            return validateLevelUpSessionCompletion(session);

          default:
            return { valid: true, errors: [] };
        }
      },

      validateSession: () => {
        const { session } = get();

        if (!session) {
          return { valid: false, errors: ['No active session'] };
        }

        return validateLevelUpSessionCompletion(session);
      },

      // ========================================================================
      // COMPLETION
      // ========================================================================

      commitLevelUp: async (onSuccess: (updatedCharacter: CharacterData) => void) => {
        const { session, character } = get();

        if (!session || !character) {
          set({ errors: ['No active session to commit'] });
          return;
        }

        set({ isLoading: true, errors: [] });

        try {
          // Final validation
          const validation = validateLevelUpSessionCompletion(session);
          if (!validation.valid) {
            set({
              errors: validation.errors,
              isLoading: false
            });
            return;
          }

          // Apply level-up to character (async due to multiclass)
          const updatedCharacter = await applyLevelUpToCharacter(character, session);

          // Clear session from both localStorage and Firebase
          clearLevelUpSession(session.characterId);
          deleteLevelUpSessionFromFirebase(session.characterId).catch(err => {
            console.error('[LevelUp] Firebase cleanup failed:', err);
          });

          // Save the updated character for retrieval on completion step
          set({
            currentStep: WizardStep.COMPLETION,
            character: updatedCharacter, // Store updated character
            isLoading: false,
            errors: []
          });

          // Call success callback to update parent component
          onSuccess(updatedCharacter);

        } catch (error) {
          set({
            errors: [(error as Error).message],
            isLoading: false
          });
        }
      },

      cancelWizard: () => {
        const { session } = get();

        if (session) {
          clearLevelUpSession(session.characterId);

          // Also delete from Firebase (async, don't wait)
          deleteLevelUpSessionFromFirebase(session.characterId).catch(err => {
            console.error('[LevelUp] Firebase cleanup failed:', err);
          });
        }

        set({
          session: null,
          currentStep: WizardStep.WELCOME,
          character: null,
          errors: [],
          warnings: []
        });
      }
    }),
    { name: 'LevelUpStore' }
  )
);
